import { useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, ArrowUpDown, Copy, Lock } from "lucide-react";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const DEFAULT_BRANDS = `Mailchimp | email marketing software | https://mailchimp.com
GetResponse | email marketing software | https://getresponse.com
Campaign Monitor | email marketing software | https://campaignmonitor.com
AWeber | email marketing software | https://aweber.com
Brevo | email marketing platform | https://brevo.com
MailerLite | email marketing tools | https://mailerlite.com
Marketo | marketing automation software | https://marketo.com
Pardot | B2B marketing automation | https://pardot.com
Keap | small business CRM automation | https://keap.com
Act-On | marketing automation platform | https://act-on.com
Ortto | marketing automation | https://ortto.com
Ahrefs | SEO tools | https://ahrefs.com
SE Ranking | SEO software | https://seranking.com
BrightEdge | enterprise SEO platform | https://brightedge.com
SpyFu | SEO competitor analysis | https://spyfu.com
Mangools | keyword research tools | https://mangools.com
Hootsuite | social media management tools | https://hootsuite.com
Sendible | social media management tools | https://sendible.com
Agorapulse | social media management tools | https://agorapulse.com
Loomly | social media scheduling tools | https://loomly.com
SocialBee | social media scheduling tools | https://socialbee.com
Heap | product analytics tools | https://heap.io
Pendo | product analytics software | https://pendo.io
Crazy Egg | heatmap tools | https://crazyegg.com
FullStory | session replay tools | https://fullstory.com
Apollo.io | sales outreach tools | https://apollo.io
Smartlead | cold email software | https://smartlead.ai
Woodpecker | cold email software | https://woodpecker.co
Mailshake | sales outreach tools | https://mailshake.com
Reply.io | sales engagement platform | https://reply.io`;

interface ScanResult {
  index: number;
  brand: string;
  keyword: string;
  url: string;
  aiTier: "Recommended" | "Recognized" | "Not found" | "Error";
  aiScore: number;
  googlePosition: number | null;
  googleFound: boolean;
  googleScore: number;
  totalScore: number;
  snippet: string;
  fullSnippet: string;
  error?: string;
}

type SortField = "index" | "brand" | "keyword" | "aiTier" | "aiScore" | "googlePosition" | "googleScore" | "totalScore";
type SortDir = "asc" | "desc";

function calcGoogleScore(position: number | null, found: boolean): number {
  if (!found || position === null) return 0;
  if (position <= 3) return 30;
  if (position <= 10) return 20;
  if (position <= 20) return 10;
  return 0;
}

function aiScoreToTier(score: number): ScanResult["aiTier"] {
  if (score >= 40) return "Recommended";
  if (score >= 20) return "Recognized";
  return "Not found";
}

const tierColors: Record<string, string> = {
  Recommended: "bg-green-100 text-green-800",
  Recognized: "bg-amber-100 text-amber-800",
  "Not found": "bg-red-100 text-red-800",
  Error: "bg-gray-100 text-gray-600",
};

function parseBrands(text: string): { brand: string; keyword: string; url: string }[] {
  const lines = text.split("\n").filter((l) => l.trim());
  const results: { brand: string; keyword: string; url: string }[] = [];
  const warnings: string[] = [];
  for (const line of lines) {
    const parts = line.split("|").map((p) => p.trim());
    if (parts.length !== 3) {
      warnings.push(`Skipped: "${line.substring(0, 40)}..." (expected 3 parts, got ${parts.length})`);
      continue;
    }
    if (parts[0] && parts[1] && parts[2]) {
      results.push({ brand: parts[0], keyword: parts[1], url: parts[2] });
    }
  }
  if (warnings.length > 0) {
    toast({ title: "Parse warnings", description: warnings.join("\n"), variant: "destructive" });
  }
  return results;
}

function toCSV(results: ScanResult[]): string {
  const header = "#,Brand,Keyword,AI Tier,AI Score,Google Position,Google Score,Total Score,ChatGPT Response";
  const rows = results.map((r) =>
    [r.index, `"${r.brand}"`, `"${r.keyword}"`, r.aiTier, r.aiScore, r.googlePosition ?? "N/A", r.googleScore, r.totalScore, `"${r.fullSnippet.replace(/"/g, '""')}"`].join(",")
  );
  return [header, ...rows].join("\n");
}

function toMarkdown(results: ScanResult[]): string {
  const header = "| # | Brand | Keyword | AI Tier | AI Score | Google Pos | Google Score | Total |";
  const sep = "|---|-------|---------|---------|----------|------------|-------------|-------|";
  const rows = results.map(
    (r) => `| ${r.index} | ${r.brand} | ${r.keyword} | ${r.aiTier} | ${r.aiScore} | ${r.googlePosition ?? "N/A"} | ${r.googleScore} | ${r.totalScore} |`
  );
  return [header, sep, ...rows].join("\n");
}

export default function BatchScanPage() {
  const [batchKey, setBatchKey] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [validating, setValidating] = useState(false);
  const [textareaValue, setTextareaValue] = useState(DEFAULT_BRANDS);
  const [results, setResults] = useState<ScanResult[]>([]);
  const [scanning, setScanning] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalBrands, setTotalBrands] = useState(0);
  const [currentBrand, setCurrentBrand] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [sortField, setSortField] = useState<SortField>("index");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [expandedSnippet, setExpandedSnippet] = useState<string | null>(null);
  const stopRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleLogin = async () => {
    const key = passwordInput.trim();
    if (!key) return;
    setValidating(true);
    setAuthError("");
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/unified-ai-visibility-scan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            "x-batch-key": key,
          },
          body: JSON.stringify({ brandName: "__auth_test__", targetKeyword: "__auth_test__" }),
        }
      );
      if (res.status === 401) {
        setAuthError("Invalid batch key");
        setValidating(false);
        return;
      }
      setBatchKey(key);
      setAuthenticated(true);
    } catch {
      setAuthError("Connection error. Try again.");
    } finally {
      setValidating(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedResults = [...results].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    const av = a[sortField];
    const bv = b[sortField];
    if (av === null && bv === null) return 0;
    if (av === null) return dir;
    if (bv === null) return -dir;
    if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir;
    return ((av as number) - (bv as number)) * dir;
  });

  const startScan = useCallback(async () => {
    const brands = parseBrands(textareaValue);
    if (brands.length === 0) {
      toast({ title: "No valid brands", description: "Check input format: brand | keyword | url", variant: "destructive" });
      return;
    }

    setResults([]);
    setScanning(true);
    setTotalBrands(brands.length);
    setCurrentIndex(0);
    setElapsed(0);
    stopRef.current = false;

    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "x-batch-key": batchKey,
    };

    for (let i = 0; i < brands.length; i++) {
      if (stopRef.current) break;

      const { brand, keyword, url } = brands[i];
      setCurrentIndex(i + 1);
      setCurrentBrand(brand);

      try {
        const [aiRes, googleRes] = await Promise.allSettled([
          fetch(`${SUPABASE_URL}/functions/v1/unified-ai-visibility-scan`, {
            method: "POST",
            headers,
            body: JSON.stringify({ brandName: brand, targetKeyword: keyword }),
          }),
          fetch(`${SUPABASE_URL}/functions/v1/google-search-visibility`, {
            method: "POST",
            headers,
            body: JSON.stringify({ brandName: brand, targetKeyword: keyword, websiteUrl: url }),
          }),
        ]);

        // Check for 401 on either
        if (aiRes.status === "fulfilled" && aiRes.value.status === 401) {
          toast({ title: "Invalid batch key", variant: "destructive" });
          setAuthenticated(false);
          setBatchKey("");
          setPasswordInput("");
          break;
        }
        if (googleRes.status === "fulfilled" && googleRes.value.status === 401) {
          toast({ title: "Invalid batch key", variant: "destructive" });
          setAuthenticated(false);
          setBatchKey("");
          setPasswordInput("");
          break;
        }

        let aiData: any = null;
        let googleData: any = null;

        if (aiRes.status === "fulfilled" && aiRes.value.ok) {
          aiData = await aiRes.value.json();
        } else if (aiRes.status === "fulfilled") {
          // Non-OK but not 401
          try { aiData = await aiRes.value.json(); } catch {}
        }

        if (googleRes.status === "fulfilled" && googleRes.value.ok) {
          googleData = await googleRes.value.json();
        } else if (googleRes.status === "fulfilled") {
          try { googleData = await googleRes.value.json(); } catch {}
        }

        const aiScore = aiData?.aiScore ?? 0;
        const fullSnippet = aiData?.rawResponseSnippet || aiData?.error || "No response";
        const googleFound = googleData?.found ?? false;
        const googlePosition = googleData?.position ?? null;
        const gScore = calcGoogleScore(googlePosition, googleFound);
        const hasError = !aiData?.success && !googleData?.success;

        const result: ScanResult = {
          index: i + 1,
          brand,
          keyword,
          url,
          aiTier: hasError ? "Error" : aiScoreToTier(aiScore),
          aiScore,
          googlePosition,
          googleFound,
          googleScore: gScore,
          totalScore: aiScore + gScore,
          snippet: fullSnippet.substring(0, 100) + (fullSnippet.length > 100 ? "…" : ""),
          fullSnippet,
          error: hasError ? "One or both scans failed" : undefined,
        };

        setResults((prev) => [...prev, result]);
      } catch (err) {
        setResults((prev) => [
          ...prev,
          {
            index: i + 1,
            brand,
            keyword,
            url,
            aiTier: "Error" as const,
            aiScore: 0,
            googlePosition: null,
            googleFound: false,
            googleScore: 0,
            totalScore: 0,
            snippet: "Network error",
            fullSnippet: String(err),
            error: String(err),
          },
        ]);
      }

      // 5-second delay between brands (unless last or stopped)
      if (i < brands.length - 1 && !stopRef.current) {
        await new Promise((r) => setTimeout(r, 5000));
      }
    }

    setScanning(false);
    if (timerRef.current) clearInterval(timerRef.current);
  }, [textareaValue, batchKey]);

  const handleStop = () => {
    stopRef.current = true;
  };

  const formatTime = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const completedResults = results.filter((r) => r.aiTier !== "Error");
  const recommended = results.filter((r) => r.aiTier === "Recommended").length;
  const recognized = results.filter((r) => r.aiTier === "Recognized").length;
  const notFound = results.filter((r) => r.aiTier === "Not found").length;
  const errors = results.filter((r) => r.aiTier === "Error").length;
  const avgScore = completedResults.length > 0 ? (completedResults.reduce((s, r) => s + r.totalScore, 0) / completedResults.length).toFixed(1) : "0.0";
  const scanComplete = !scanning && results.length > 0;

  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => handleSort(field)}>
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className="h-3 w-3 opacity-40" />
      </span>
    </TableHead>
  );

  // Password screen
  if (!authenticated) {
    return (
      <>
        <Helmet>
          <title>Batch Scan — Internal</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <Lock className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <CardTitle className="text-xl">Batch Scan — Internal Research Tool</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                type="password"
                placeholder="Enter batch key"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setAuthError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                disabled={validating}
              />
              {authError && <p className="text-sm text-destructive">{authError}</p>}
              <Button className="w-full" onClick={handleLogin} disabled={validating}>
                {validating ? "Validating..." : "Enter"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Batch Scan — Internal</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-background p-6 max-w-[1400px] mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Batch Scan — Internal Research Tool</h1>

        {/* Warning banner */}
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Keep this tab open and active during scanning. Switching tabs may cause browser throttling that pauses the scan.</span>
        </div>

        {/* Input area */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <Textarea
              className="font-mono text-xs min-h-[300px]"
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
              disabled={scanning}
            />
            <div className="flex gap-2 items-center">
              <Button onClick={startScan} disabled={scanning}>
                Start Batch Scan
              </Button>
              {scanning && (
                <Button variant="secondary" onClick={handleStop}>
                  Stop
                </Button>
              )}
              <button
                className="text-xs text-muted-foreground underline ml-2"
                onClick={() => setTextareaValue("")}
                disabled={scanning}
              >
                Clear
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Progress */}
        {scanning && (
          <Card>
            <CardContent className="pt-6 space-y-2">
              <div className="text-sm font-medium">
                Scanning {currentIndex}/{totalBrands}: {currentBrand}…
              </div>
              <Progress value={(currentIndex / totalBrands) * 100} className="h-2" />
              <div className="text-xs text-muted-foreground">
                Elapsed: {formatTime(elapsed)} · {Math.round((currentIndex / totalBrands) * 100)}%
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary stats */}
        {scanComplete && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold">{results.length}</div><div className="text-xs text-muted-foreground">Total scanned</div></CardContent></Card>
            <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold text-green-600">{recommended}</div><div className="text-xs text-muted-foreground">Recommended ({results.length ? Math.round((recommended / results.length) * 100) : 0}%)</div></CardContent></Card>
            <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold text-amber-600">{recognized}</div><div className="text-xs text-muted-foreground">Recognized ({results.length ? Math.round((recognized / results.length) * 100) : 0}%)</div></CardContent></Card>
            <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold text-red-600">{notFound}</div><div className="text-xs text-muted-foreground">Not found ({results.length ? Math.round((notFound / results.length) * 100) : 0}%)</div></CardContent></Card>
            <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold text-gray-500">{errors}</div><div className="text-xs text-muted-foreground">Errors</div></CardContent></Card>
            <Card><CardContent className="pt-4 text-center"><div className="text-2xl font-bold">{avgScore}</div><div className="text-xs text-muted-foreground">Avg total score</div></CardContent></Card>
          </div>
        )}

        {/* Results table */}
        {results.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortHeader field="index">#</SortHeader>
                      <SortHeader field="brand">Brand</SortHeader>
                      <SortHeader field="keyword">Keyword</SortHeader>
                      <SortHeader field="aiTier">AI Tier</SortHeader>
                      <SortHeader field="aiScore">AI Score</SortHeader>
                      <SortHeader field="googlePosition">Google Pos</SortHeader>
                      <SortHeader field="googleScore">Google Score</SortHeader>
                      <SortHeader field="totalScore">Total</SortHeader>
                      <TableHead>ChatGPT Response</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedResults.map((r) => (
                      <TableRow key={r.index}>
                        <TableCell className="font-mono text-xs">{r.index}</TableCell>
                        <TableCell className="font-medium">{r.brand}</TableCell>
                        <TableCell className="text-sm">{r.keyword}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={tierColors[r.aiTier]}>
                            {r.aiTier}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{r.aiScore}</TableCell>
                        <TableCell className="font-mono">{r.googleFound ? r.googlePosition : "N/A"}</TableCell>
                        <TableCell className="font-mono">{r.googleScore}</TableCell>
                        <TableCell className="font-mono font-bold">{r.totalScore}</TableCell>
                        <TableCell className="max-w-[200px]">
                          <button
                            className="text-xs text-left truncate block w-full hover:text-primary"
                            onClick={() => setExpandedSnippet(r.fullSnippet)}
                            title="Click to expand"
                          >
                            {r.snippet}
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Export buttons */}
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(toCSV(results));
                    toast({ title: "Copied!", description: "CSV copied to clipboard" });
                  }}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy as CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(toMarkdown(results));
                    toast({ title: "Copied!", description: "Markdown table copied to clipboard" });
                  }}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy as Markdown
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Snippet modal */}
        <Dialog open={!!expandedSnippet} onOpenChange={() => setExpandedSnippet(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Full ChatGPT Response</DialogTitle>
            </DialogHeader>
            <p className="text-sm whitespace-pre-wrap">{expandedSnippet}</p>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
